import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { apiClient, formatCurrency, MONTHS, TRANSACTION_TYPES, getCategories } from '../lib/api';
import { toast } from 'sonner';
import {
  Plus, Edit, Trash2, CheckCircle2, Clock, AlertCircle, ChevronLeft, ChevronRight,
  TrendingUp, TrendingDown, PiggyBank, Banknote, RefreshCw,
} from 'lucide-react';
import { format } from 'date-fns';
import { it as itLocale } from 'date-fns/locale';
import ConfirmDialog from '../components/ConfirmDialog';
import EmptyState from '../components/EmptyState';
import MemberPicker from '../components/MemberPicker';

const FREQUENCIES = ['Mensile', 'Bimestrale', 'Trimestrale', 'Semestrale', 'Annuale'];

const TYPE_META = {
  REDDITO:  { label: 'Entrata',   icon: TrendingUp,   accent: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-100 dark:bg-emerald-900/30' },
  SPESE:    { label: 'Uscita',    icon: TrendingDown, accent: 'text-red-500',                            bg: 'bg-red-100 dark:bg-red-900/30' },
  RISPARMI: { label: 'Risparmio', icon: PiggyBank,    accent: 'text-blue-500',                           bg: 'bg-blue-100 dark:bg-blue-900/30' },
  DEBITI:   { label: 'Debito',    icon: Banknote,     accent: 'text-orange-500',                         bg: 'bg-orange-100 dark:bg-orange-900/30' },
};

const STATUS_META = {
  upcoming:  { label: 'In arrivo',  pill: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',         icon: Clock },
  in_window: { label: 'Atteso ora', pill: 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200', icon: AlertCircle },
  overdue:   { label: 'In ritardo', pill: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',         icon: AlertCircle },
  confirmed: { label: 'Confermato', pill: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200', icon: CheckCircle2 },
};

const RecurringBillsPage = () => {
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState('REDDITO');

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '', amount: '', type: 'SPESE', category_id: '',
    due_day: 1, due_day_end: '',
    frequency: 'Mensile',
    expected_min: '', expected_max: '', notes: '',
    assignees: [],
  });

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [confirmForm, setConfirmForm] = useState({ date: '', amount: '', description: '' });

  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [statusRes, catsRes] = await Promise.all([
        apiClient.get(`/recurring-bills/status?month=${month}&year=${year}`),
        getCategories(),
      ]);
      setItems(statusRes.data);
      setCategories(catsRes.data);
    } catch (e) {
      toast.error('Errore caricamento movimenti');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [month, year]);

  const changeMonth = (d) => {
    let m = month + d, y = year;
    if (m < 1) { m = 12; y--; } else if (m > 12) { m = 1; y++; }
    setMonth(m); setYear(y);
  };

  const itemsByType = useMemo(() => {
    const split = { REDDITO: [], SPESE: [], RISPARMI: [], DEBITI: [] };
    items.forEach((i) => { (split[i.type] || split.SPESE).push(i); });
    return split;
  }, [items]);

  const totalsByType = useMemo(() => {
    const t = { REDDITO: 0, SPESE: 0, RISPARMI: 0, DEBITI: 0 };
    items.forEach((i) => {
      t[i.type] += (i.actual_amount ?? i.amount);
    });
    return t;
  }, [items]);

  const expectedFlow = totalsByType.REDDITO - totalsByType.SPESE - totalsByType.RISPARMI - totalsByType.DEBITI;

  const openCreate = (type = 'SPESE') => {
    setEditing(null);
    setForm({
      name: '', amount: '', type, category_id: '',
      due_day: 1, due_day_end: '',
      frequency: 'Mensile',
      expected_min: '', expected_max: '', notes: '',
      assignees: [],
    });
    setFormOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name,
      amount: String(item.amount),
      type: item.type,
      category_id: item.category_id || '',
      due_day: item.due_day,
      due_day_end: item.due_day_end || '',
      frequency: item.frequency,
      expected_min: item.expected_min ?? '',
      expected_max: item.expected_max ?? '',
      notes: item.notes || '',
      assignees: item.assignees || [],
    });
    setFormOpen(true);
  };

  const submitForm = async (e) => {
    e.preventDefault();
    if (!form.name || !form.amount || !form.category_id) {
      toast.error('Compila nome, importo e categoria');
      return;
    }
    const payload = {
      name: form.name,
      amount: parseFloat(form.amount),
      type: form.type,
      category_id: form.category_id,
      due_day: parseInt(form.due_day, 10),
      due_day_end: form.due_day_end ? parseInt(form.due_day_end, 10) : null,
      frequency: form.frequency,
      expected_min: form.expected_min ? parseFloat(form.expected_min) : null,
      expected_max: form.expected_max ? parseFloat(form.expected_max) : null,
      notes: form.notes || null,
      assignees: form.assignees && form.assignees.length > 0 ? form.assignees : null,
    };
    try {
      if (editing) {
        await apiClient.put(`/recurring-bills/${editing.id}`, payload);
        toast.success('Movimento aggiornato');
      } else {
        await apiClient.post('/recurring-bills', payload);
        toast.success('Movimento creato');
      }
      setFormOpen(false);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore salvataggio');
    }
  };

  const openConfirm = (item) => {
    setConfirmTarget(item);
    let defaultDate = item.due_date_start;
    const todayStr = format(new Date(), 'yyyy-MM-dd');
    if (todayStr >= item.due_date_start && todayStr <= item.due_date_end) {
      defaultDate = todayStr;
    } else if (todayStr > item.due_date_end) {
      defaultDate = item.due_date_end;
    }
    setConfirmForm({
      date: item.actual_date || defaultDate,
      amount: String(item.actual_amount ?? item.amount),
      description: '',
    });
    setConfirmOpen(true);
  };

  const submitConfirm = async (e) => {
    e.preventDefault();
    if (!confirmTarget) return;
    try {
      await apiClient.post(`/recurring-bills/${confirmTarget.id}/confirm`, {
        date: confirmForm.date,
        amount: parseFloat(confirmForm.amount),
        description: confirmForm.description || null,
      });
      toast.success(confirmTarget.actual_amount ? 'Importo aggiornato' : 'Movimento confermato');
      setConfirmOpen(false);
      setConfirmTarget(null);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore conferma');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await apiClient.delete(`/recurring-bills/${deleteTarget.id}`);
      toast.success('Movimento eliminato');
      setDeleteTarget(null);
      loadAll();
    } catch {
      toast.error('Errore eliminazione');
    }
  };

  const formatRange = (item) => {
    if (item.due_day_end && item.due_day_end !== item.due_day) {
      return `tra il ${item.due_day} e il ${item.due_day_end} del mese`;
    }
    return `il ${item.due_day} del mese`;
  };

  const formatDueLabel = (item) => {
    const start = new Date(item.due_date_start + 'T00:00');
    const end = new Date(item.due_date_end + 'T00:00');
    if (!item.due_day_end || item.due_day === item.due_day_end) {
      return format(start, "d MMM yyyy", { locale: itLocale });
    }
    return `${format(start, 'd MMM', { locale: itLocale })} – ${format(end, 'd MMM yyyy', { locale: itLocale })}`;
  };

  const ItemCard = ({ item }) => {
    const meta = TYPE_META[item.type] || TYPE_META.SPESE;
    const status = STATUS_META[item.status] || STATUS_META.upcoming;
    const Icon = meta.icon;
    const StatusIcon = status.icon;
    const Money = ({ value }) => (
      <span className={`tabular-nums font-semibold ${meta.accent}`}>
        {item.type === 'REDDITO' ? '+' : '−'}{formatCurrency(Math.abs(value))}
      </span>
    );

    return (
      <Card className="card-hover" data-testid={`recurring-card-${item.id}`}>
        <CardContent className="pt-5">
          <div className="flex items-start gap-4">
            <div className={`w-11 h-11 rounded-xl ${meta.bg} flex items-center justify-center shrink-0`}>
              <Icon className={`w-5 h-5 ${meta.accent}`} strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-semibold truncate">{item.name}</h3>
                  <p className="text-xs text-muted-foreground">
                    {item.category_name} · {item.frequency.toLowerCase()}
                  </p>
                </div>
                <Badge className={`${status.pill} border-0 shrink-0`}>
                  <StatusIcon className="w-3 h-3 mr-1" /> {status.label}
                </Badge>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    {item.status === 'confirmed' ? 'Effettivo' : 'Atteso'}
                  </p>
                  {item.status === 'confirmed' ? (
                    <Money value={item.actual_amount} />
                  ) : (
                    <Money value={item.amount} />
                  )}
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Data</p>
                  <p className="text-sm font-medium">
                    {item.status === 'confirmed'
                      ? format(new Date(item.actual_date + 'T00:00'), 'd MMM yyyy', { locale: itLocale })
                      : formatDueLabel(item)}
                  </p>
                </div>
              </div>

              {item.assignee_names && item.assignee_names.length > 0 && (
                <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                  <span>Intestatar{item.assignee_names.length > 1 ? 'i' : 'o'}:</span>
                  <span>{item.assignee_names.join(' · ')}</span>
                </div>
              )}
              {(item.expected_min || item.expected_max) && item.status !== 'confirmed' && (
                <p className="text-[11px] text-muted-foreground mt-2">
                  Range tipico: {formatCurrency(item.expected_min || 0)} – {formatCurrency(item.expected_max || item.amount)}
                </p>
              )}
              {item.notes && (
                <p className="text-[11px] text-muted-foreground italic mt-1 truncate">{item.notes}</p>
              )}

              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <Button
                  size="sm"
                  variant={item.status === 'confirmed' ? 'outline' : 'default'}
                  onClick={() => openConfirm(item)}
                  data-testid={`confirm-btn-${item.id}`}
                >
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                  {item.status === 'confirmed' ? 'Modifica importo' : 'Conferma valore'}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => openEdit(item)} data-testid={`edit-btn-${item.id}`}>
                  <Edit className="w-3.5 h-3.5 mr-1.5" /> Regola
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(item)} className="text-destructive">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const Section = ({ type }) => {
    const list = itemsByType[type] || [];
    const meta = TYPE_META[type];
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className={`text-sm font-semibold uppercase tracking-wider ${meta.accent}`}>
            {meta.label}{list.length !== 1 ? 'e' : ''} · {list.length}
          </h2>
          <Button size="sm" variant="outline" onClick={() => openCreate(type)} data-testid={`add-${type}`}>
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Aggiungi
          </Button>
        </div>
        {list.length === 0 ? (
          <EmptyState
            title={`Nessun ${meta.label.toLowerCase()} ricorrente`}
            description={
              type === 'REDDITO'
                ? "Aggiungi il tuo stipendio o pensione per tenere traccia delle entrate fisse, anche con range di giorni."
                : "Aggiungi affitto, bollette o abbonamenti per non perdere scadenze."
            }
            action={{ label: 'Aggiungi', icon: Plus, onClick: () => openCreate(type) }}
          />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {list.map((item) => <ItemCard key={item.id} item={item} />)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-in" data-testid="recurring-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Movimenti ricorrenti</h1>
          <p className="text-muted-foreground">
            Stipendi, pensioni, affitti, bollette e abbonamenti che si ripetono ogni periodo.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => changeMonth(-1)} data-testid="rec-prev-month">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="px-3 py-2 bg-card border rounded-lg min-w-[180px] text-center font-medium">
            {MONTHS[month - 1]} {year}
          </div>
          <Button variant="outline" size="icon" onClick={() => changeMonth(1)} data-testid="rec-next-month">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={loadAll}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Sommario flusso atteso */}
      <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
        <CardContent className="pt-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Entrate</p>
              <p className="text-xl font-bold tabular-nums text-emerald-600 dark:text-emerald-400">
                +{formatCurrency(totalsByType.REDDITO)}
              </p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Uscite</p>
              <p className="text-xl font-bold tabular-nums text-red-500">
                −{formatCurrency(totalsByType.SPESE)}
              </p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Risparmi</p>
              <p className="text-xl font-bold tabular-nums text-blue-500">
                −{formatCurrency(totalsByType.RISPARMI)}
              </p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Debiti</p>
              <p className="text-xl font-bold tabular-nums text-orange-500">
                −{formatCurrency(totalsByType.DEBITI)}
              </p>
            </div>
            <div className="col-span-2 md:col-span-1 md:text-right md:border-l md:pl-4">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Flusso atteso</p>
              <p className={`text-xl font-bold tabular-nums ${expectedFlow >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`}>
                {expectedFlow >= 0 ? '+' : ''}{formatCurrency(expectedFlow)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-4 max-w-2xl">
          {Object.keys(TYPE_META).map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`tab-${t}`}>
              {TYPE_META[t].label}{itemsByType[t]?.length > 0 ? <span className="ml-1.5 text-xs opacity-60">({itemsByType[t].length})</span> : null}
            </TabsTrigger>
          ))}
        </TabsList>
        {Object.keys(TYPE_META).map((t) => (
          <TabsContent key={t} value={t} className="mt-6">
            {loading ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 animate-pulse">
                {[1, 2, 3, 4].map((i) => <div key={i} className="h-32 bg-muted rounded-xl" />)}
              </div>
            ) : (
              <Section type={t} />
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Form dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editing ? 'Modifica movimento ricorrente' : 'Nuovo movimento ricorrente'}
            </DialogTitle>
            <DialogDescription>
              Imposta importo medio e una finestra di giorni in cui il movimento è atteso.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitForm} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2 sm:col-span-2">
                <Label>Nome</Label>
                <Input
                  placeholder="es. Stipendio Marco"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v, category_id: '' })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TRANSACTION_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Categoria</Label>
                <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                  <SelectContent>
                    {categories.filter((c) => c.type === form.type).map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Importo medio (€)</Label>
                <Input
                  type="number" step="0.01" min="0.01"
                  placeholder="es. 2400"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  required
                  inputMode="decimal"
                />
              </div>
              <div className="space-y-2">
                <Label>Importo minimo (opz.)</Label>
                <Input
                  type="number" step="0.01" min="0"
                  placeholder="2300"
                  value={form.expected_min}
                  onChange={(e) => setForm({ ...form, expected_min: e.target.value })}
                  inputMode="decimal"
                />
              </div>
              <div className="space-y-2">
                <Label>Importo massimo (opz.)</Label>
                <Input
                  type="number" step="0.01" min="0"
                  placeholder="2700"
                  value={form.expected_max}
                  onChange={(e) => setForm({ ...form, expected_max: e.target.value })}
                  inputMode="decimal"
                />
              </div>
              <div className="space-y-2">
                <Label>Giorno (da)</Label>
                <Input
                  type="number" min="1" max="31"
                  value={form.due_day}
                  onChange={(e) => setForm({ ...form, due_day: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Giorno (a) — opz.</Label>
                <Input
                  type="number" min="1" max="31"
                  placeholder="lascia vuoto per giorno fisso"
                  value={form.due_day_end}
                  onChange={(e) => setForm({ ...form, due_day_end: e.target.value })}
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Frequenza</Label>
                <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FREQUENCIES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Note (opz.)</Label>
                <Textarea
                  placeholder="es. Stipendio variabile per straordinari"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="sm:col-span-2">
                <MemberPicker
                  value={form.assignees}
                  onChange={(arr) => setForm({ ...form, assignees: arr })}
                  label="A chi è intestato?"
                  help="Per stipendi/pensioni seleziona la persona che lo riceve. Per affitto/mutuo seleziona chi lo paga (split equo)."
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setFormOpen(false)}>Annulla</Button>
              <Button type="submit">{editing ? 'Salva modifiche' : 'Crea movimento'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirm-actual dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {confirmTarget?.actual_amount ? 'Modifica valore effettivo' : 'Conferma valore effettivo'}
            </DialogTitle>
            <DialogDescription>
              {confirmTarget && (
                <>
                  <strong>{confirmTarget.name}</strong> · {MONTHS[month - 1]} {year}
                  <br />
                  Atteso: <strong className="text-foreground">{formatCurrency(confirmTarget.amount)}</strong> {formatRange(confirmTarget)}
                  {(confirmTarget.expected_min || confirmTarget.expected_max) && (
                    <span className="text-xs">
                      {' '}(range {formatCurrency(confirmTarget.expected_min || 0)} – {formatCurrency(confirmTarget.expected_max || confirmTarget.amount)})
                    </span>
                  )}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitConfirm} className="space-y-4">
            <div className="space-y-2">
              <Label>Data effettiva</Label>
              <Input
                type="date"
                value={confirmForm.date}
                onChange={(e) => setConfirmForm({ ...confirmForm, date: e.target.value })}
                required
                data-testid="confirm-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Importo effettivo (€)</Label>
              <Input
                type="number" step="0.01" min="0.01"
                value={confirmForm.amount}
                onChange={(e) => setConfirmForm({ ...confirmForm, amount: e.target.value })}
                required
                data-testid="confirm-amount-input"
                inputMode="decimal"
              />
              {confirmTarget && parseFloat(confirmForm.amount) && parseFloat(confirmForm.amount) !== confirmTarget.amount && (
                <p className="text-xs text-muted-foreground">
                  {parseFloat(confirmForm.amount) > confirmTarget.amount ? '+' : ''}
                  {(((parseFloat(confirmForm.amount) - confirmTarget.amount) / confirmTarget.amount) * 100).toFixed(1)}% rispetto al medio
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Note (opz.)</Label>
              <Input
                placeholder="es. Stipendio + premio produzione"
                value={confirmForm.description}
                onChange={(e) => setConfirmForm({ ...confirmForm, description: e.target.value })}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setConfirmOpen(false)}>Annulla</Button>
              <Button type="submit" data-testid="confirm-submit-btn">
                <CheckCircle2 className="w-4 h-4 mr-2" />
                {confirmTarget?.actual_amount ? 'Aggiorna' : 'Conferma'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Eliminare il movimento ricorrente?"
        description={
          deleteTarget &&
          `"${deleteTarget.name}" verrà rimosso. Le transazioni già confermate restano.`
        }
        confirmLabel="Elimina"
        destructive
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default RecurringBillsPage;
